import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  type TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { injectCurrentUserPreferencesContract } from '@core/account-contracts';
import { LocalStorageService } from '@core/common-legacy';
import {
  AiLogoComponent,
  ButtonComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  IconName,
  IconToken,
} from '@core/user-interface-legacy';
import { SubscriptionService } from '@recruitee/company-logic';

export type FeatureDiscoveryBannerVariant = 'default' | 'ai';

@Component({
  selector: 'rt-feature-discovery-banner',
  imports: [
    AiLogoComponent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    HeadingComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './feature-discovery-banner.component.html',
  styleUrl: './feature-discovery-banner.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.is-visible]': 'isVisible()',
    '[class.ai]': "variant() === 'ai'",
  },
})
export class FeatureDiscoveryBannerComponent {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly subscriptionService = inject(SubscriptionService);

  public componentStorageKey = input<string>();
  public title = input.required<string>();
  public description = input.required<string>();
  public iconName = input<IconName>();
  public iconColor = input<IconToken>('colorIconPrimaryDefault');
  public ctaText = input.required<string>();
  public variant = input<FeatureDiscoveryBannerVariant>('default');
  public textTemplate = input<
    TemplateRef<{
      title: string;
      description: string;
    }>
  >();
  public dismissible = input<boolean>(true);

  public ctaClick = output<void>();
  public dismissClick = output<void>();

  public isVisible = signal<boolean>(false);
  public themeName = toSignal(injectCurrentUserPreferencesContract().activeThemeName$);

  public ngOnInit(): void {
    this.isVisible.set(this.getBannerVisibility());
  }

  public dismiss(): void {
    this.isVisible.set(false);
    if (this.componentStorageKey()) {
      this.localStorageService.set(this.componentStorageKey()!, {
        visible: false,
      });
    }
    this.dismissClick.emit();
  }

  private getBannerVisibility(): boolean {
    if (!this.componentStorageKey()) {
      return true;
    }
    if (this.subscriptionService.isTrial()) return false;

    const { visible } = this.localStorageService.get(this.componentStorageKey()!, {
      visible: true,
    });
    return visible;
  }
}
